import {
    APP_SUMO_FREE_TIER_QUOTA,
    BILLING_LIVE_STATUSES,
    getProductConfig,
    getTierConfig,
} from "../services/billing/billing.const"
import { IS_STRIPE_TEST_MODE } from "../stripe"
import { isOrgAppSumo } from "./org-plan"

/**
 * Product-agnostic reads of an add-on's state on the org document. Every add-on
 * (Purchasing, Expenses, Agents...) stores the same shape under its own `orgPath`, so
 * the tier/usage resolution is the same code with a different key.
 *
 * Display only, like billing.const.js - the API decides what is actually billed and
 * enforced. utils/purchasing-plan.js and utils/expenses-plan.js are the per-product
 * front doors; prefer those in components so the product id stays in one place.
 */

/**
 * The org's Stripe state for this add-on, for the Stripe account this build talks to.
 * The API keeps live-account state in <orgPath>.stripe and test-account state in
 * <orgPath>.stripeTest (localhost runs the production DB against Stripe's test account,
 * and ids from one account mean nothing to the other), choosing by its secret key. This
 * chooses by the matching publishable key, so both sides always read the same slot.
 *
 * @param {object} orgData
 * @param {string} product One of BILLING_PRODUCT
 * @returns {object} The stripe sub-document for this mode, or {} when there is none.
 */
export const getAddonStripe = (orgData, product) => {
    const orgPath = getProductConfig(product)?.orgPath
    if (!orgPath) return {}

    return orgData?.[orgPath]?.[IS_STRIPE_TEST_MODE ? "stripeTest" : "stripe"] || {}
}

/**
 * Whether the org holds a live Stripe subscription to this add-on - a trial included.
 * past_due counts as live so a failed renewal degrades to a billing warning rather than
 * an instant downgrade.
 *
 * @param {object} orgData
 * @param {string} product
 * @returns {boolean}
 */
export const isAddonSubscribed = (orgData, product) =>
    BILLING_LIVE_STATUSES.includes(getAddonStripe(orgData, product).subscriptionStatus)

/**
 * Whether the add-on subscription is in its free trial. The tile, the Usage tab and the
 * quota all read differently for its duration - see ADDON_TRIAL_QUOTA.
 *
 * @param {object} orgData
 * @param {string} product
 * @returns {boolean}
 */
export const isAddonTrialing = (orgData, product) =>
    getAddonStripe(orgData, product).subscriptionStatus === "trialing"

/**
 * The tier granted from MxAdmin (outside Stripe), if any - an Enterprise customer
 * invoiced directly, or an org kept going by hand after a trial. Mirrors
 * orgLicense.getOrgAddonManualTier() in the API. Nothing about it can be bought,
 * changed or cancelled from the app.
 *
 * @param {object} orgData
 * @param {string} product
 * @returns {object|undefined} A tier entry from billing.const.js
 */
export const getAddonManualTier = (orgData, product) => {
    const orgPath = getProductConfig(product)?.orgPath

    return orgPath ? getTierConfig(product, orgData?.[orgPath]?.manualTier) : undefined
}

export const isAddonManual = (orgData, product) => !!getAddonManualTier(orgData, product)

/**
 * Whether the org holds the add-on at all, bought or granted.
 *
 * @param {object} orgData
 * @param {string} product
 * @returns {boolean}
 */
export const hasAddon = (orgData, product) => isAddonSubscribed(orgData, product) || isAddonManual(orgData, product)

/**
 * The tier the org is effectively on: the subscribed one when the subscription is live
 * and the tier is known, otherwise a manual grant, otherwise the Free allowance bundled
 * with a paid plan. Same order as orgLicense.getOrgAddonTier() in the API.
 *
 * @param {object} orgData
 * @param {string} product
 * @returns {object} A tier entry from billing.const.js, or the product's free tier.
 */
export const getAddonTier = (orgData, product) => {
    if (isAddonSubscribed(orgData, product)) {
        const tier = getTierConfig(product, getAddonStripe(orgData, product).tier)
        if (tier) return tier
    }

    return getAddonManualTier(orgData, product) || getProductConfig(product)?.freeTier
}

/**
 * The bundled Free allowance for this add-on, for this org. Everyone gets the product's
 * freeTier.includedQuota except AppSumo orgs, which get the smaller
 * APP_SUMO_FREE_TIER_QUOTA - see that constant for why. Mirrors the Free branch of
 * orgLicense.getOrgAddonQuota() in the API, which is what actually refuses the write.
 *
 * Only ever the FREE allowance: an org on a paid tier gets that tier's quota whatever its
 * licence came from, so callers must resolve the tier first (getAddonUsage does).
 *
 * @param {object} orgData
 * @param {string} product One of BILLING_PRODUCT
 * @returns {number}
 */
export const getAddonFreeQuota = (orgData, product) =>
    isOrgAppSumo(orgData) ? APP_SUMO_FREE_TIER_QUOTA : getProductConfig(product)?.freeTier?.includedQuota || 0

/**
 * This period's usage against the tier's quota. On a paid tier `quota` prefers what the
 * API stamped on the org at subscription time and falls back to the tier config; on Free
 * it comes from getAddonFreeQuota (nothing is ever stamped for Free, and the allowance
 * is smaller for AppSumo orgs).
 *
 * `used` is only present for products the API mirrors onto the org document - Purchasing
 * does, Expenses is counted from the expense collection on request - so callers that
 * need a live count should read it from the usage endpoint instead.
 *
 * @param {object} orgData
 * @param {string} product
 * @returns {{ tier: object, used: number, quota: number, remaining: number, overage: number }}
 */
export const getAddonUsage = (orgData, product) => {
    const tier = getAddonTier(orgData, product)
    const config = getProductConfig(product)
    const orgPath = config?.orgPath
    const usage = (orgPath && orgData?.[orgPath]?.usage) || {}
    // A trial is capped for its whole length, not per month, so it counts against
    // `trialCount` - the running total the monthly reset never clears - rather than the
    // calendar-month counter. Mirrors orgLicense.getOrgAddonUsed() in the API, dual-read
    // included: trials already running when trialCount was added have only the month's.
    const used = isAddonTrialing(orgData, product)
        ? Math.max(usage.trialCount || 0, usage.currentPeriodCount || 0)
        : usage.currentPeriodCount || 0
    // A trial runs under its own hard cap, whatever the tier's quota says - the same
    // rule orgLicense.getOrgAddonQuota applies server-side.
    const quota = isAddonFreeTier(orgData, product)
        ? getAddonFreeQuota(orgData, product)
        : isAddonTrialing(orgData, product)
          ? config?.trialQuota || 0
          : usage.includedQuota || tier?.includedQuota || 0

    return {
        tier,
        used,
        quota,
        remaining: Math.max(quota - used, 0),
        overage: Math.max(used - quota, 0),
    }
}

/**
 * @param {object} orgData
 * @param {string} product
 * @returns {boolean} True when the org is on the bundled Free allowance, not a paid tier.
 */
export const isAddonFreeTier = (orgData, product) =>
    getAddonTier(orgData, product)?.id === getProductConfig(product)?.freeTier?.id
